import React from 'react';
import { Button, Stack, styled, Typography, Tabs, Tab } from '@mui/material';
import { HeaderClient } from './conponents/HeaderClient';
import { TableInfiniteScroll } from 'app/components/elements/TableInfiniteScroll';
import { HEADER_CLIENTS, useInjection } from './hook';
import { Client } from 'types/clients';
import { DialogConfirm } from '../../components/elements/DialogConfirm';
import { TextEllipsis } from '../../components/elements/TextEllipsis';
import { ClientLogo } from './conponents/ClientPhoto';
import { useAuth } from 'contexts/auth';
import { Role } from 'const/common';
import { EditClientModal } from './conponents/EditClientModal';

export const ClientsPage = () => {
  const {
    tab,
    setTab,
    clients,
    fetchMoreData,
    hasMore,
    handleSearch,
    onSortHeader,
    searchParams,
    navigateToCreateCline,
    onClickAction,
    clientTarget,
    clientAction,
    handleArchiveClient,
    handleActiveClient,
    handleDelete,
    handleDeleteClientClick,
    clientEdit,
    setClientEdit,
    handleEditClient,
    handleSubmitEditClient,
    deleteStep,
    resetDeleteFlow
  } = useInjection();
  const { auth } = useAuth();
  const isSuperAdmin = auth?.role === Role.SUPER_ADMIN;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderDataMap = (clients: Client[]): any[] => {
    const isCanAccessRoom = auth?.role
      ? [Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role)
      : false;
    const isAllowPermission = (clientId: number) =>
      isCanAccessRoom ? true : auth?.accessibleClients?.some((client) => client.id === clientId);
    return clients.map((client) => {
      if (!isAllowPermission(client.id)) return null;
      return {
        clientName: {
          value: (
            <Stack flexDirection={'row'} alignItems={'center'} gap={'10px'}>
              {client.logo && <ClientLogo url={client.logo} alt={'logo-client'} height={20} />}
              <TextEllipsis
                onClick={() => {
                  if (isAllowPermission(client.id))
                    onClickAction(`/clients/${client.clientName}/${client.id}/location`);
                }}
                text={client.clientName}
                styles={{ textDecoration: 'underline', cursor: 'pointer', color: '#226AAA' }}
                width={'80%'}
              />
            </Stack>
          )
        },
        clientEmail: {
          value: <Typography>{client.clientEmail}</Typography>
        },
        contactPerson: {
          value: <Typography>{client.contactPerson}</Typography>
        },
        actions: {
          value: isAllowPermission(client.id) && (
            <Stack flexDirection="row" gap={1.25}>
              <Button
                onClick={() =>
                  onClickAction(`/clients/${client.clientName}/${client.id}/location`)
                }>
                <Typography sx={{ textDecoration: 'underline' }}>View Locations</Typography>
              </Button>

              {isSuperAdmin && tab === 'active' && (
                <>
                  <Button onClick={() => handleEditClient(client)}>
                    <Typography sx={{ textDecoration: 'underline', color: '#2563eb' }}>
                      Edit
                    </Typography>
                  </Button>

                  <Button onClick={() => handleArchiveClient(client)}>
                    <Typography sx={{ textDecoration: 'underline', color: '#d97706' }}>
                      Archive
                    </Typography>
                  </Button>
                </>
              )}

              {isSuperAdmin && tab === 'archived' && (
                <>
                  <Button onClick={() => handleActiveClient(client)}>
                    <Typography sx={{ textDecoration: 'underline', color: '#16a34a' }}>
                      Active
                    </Typography>
                  </Button>

                  <Button onClick={() => handleDeleteClientClick(client)}>
                    <Typography sx={{ textDecoration: 'underline', color: '#dc2626' }}>
                      Delete
                    </Typography>
                  </Button>
                </>
              )}
            </Stack>
          )
        }
      };
    });
  };
  return (
    <WrapperCreatePage gap={2.5}>
      <HeaderClient
        // valueFilterType={searchParams.get('type') || ''}
        onSearch={handleSearch}
        // onFilterType={filterByTypeAccess}
        // optionFilterType={optionTypeAccess}
        textButton={'Add New Client'}
        paths={[{ name: 'Clients', onClick: () => {}, level: 999 }]}
        title={'Clients'}
        onClickButton={navigateToCreateCline}
        isCanAccessRoom={[Role.ADMIN, Role.SUPER_ADMIN].includes(auth?.role || '')}
      />
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{ borderBottom: '1px solid #eee' }}>
        <Tab label="Clients" value="active" />
        <Tab label="Archived Clients" value="archived" />
      </Tabs>
      <Stack id={'id-table-clients'} sx={{ height: 'calc(100% - 230px)', overflow: 'scroll' }}>
        <TableInfiniteScroll
          sort={{
            field: searchParams.get('sortBy') || '',
            order: searchParams.get('order') || ''
          }}
          onSortHeader={onSortHeader}
          idElementScroll={'id-table-clients'}
          data={renderDataMap(clients)}
          handleFetchMore={fetchMoreData}
          headers={HEADER_CLIENTS}
          hasMore={hasMore}
          handleChooseRow={() => {}}
          styledTableRow={{ cursor: ' default ' }}
        />
      </Stack>
      <DialogConfirm
        isOpen={Boolean(clientTarget)}
        onClose={resetDeleteFlow}
        textTitle={
          clientAction === 'delete'
            ? deleteStep === 'warning1'
              ? 'Delete Client – Warning'
              : 'Confirm Client Deletion'
            : clientAction === 'archive'
              ? 'Archive Client'
              : 'Active Client'
        }
        textContent={
          clientAction === 'delete'
            ? deleteStep === 'warning1'
              ? 'Deleting client will erase all data for the client. Are you sure you want to proceed?'
              : `This will result in client deletion. Confirm that this is what you want to do. Client: ${clientTarget?.clientName}`
            : `Are you sure you want to ${
                clientAction === 'archive' ? 'archive' : 'activate'
              } this client ${clientTarget?.clientName}?`
        }
        onSubmit={handleDelete}
        isDelete={clientAction === 'delete'}
        textButton={
          clientAction === 'delete'
            ? deleteStep === 'warning1'
              ? 'Proceed'
              : 'Delete'
            : clientAction === 'archive'
              ? 'Archive'
              : 'Active'
        }
      />
      <EditClientModal
        open={Boolean(clientEdit)}
        client={clientEdit}
        onClose={() => setClientEdit(undefined)}
        onSubmit={handleSubmitEditClient}
      />
    </WrapperCreatePage>
  );
};

const WrapperCreatePage = styled(Stack)(() => ({
  padding: '40px 30px 30px 40px',
  width: '100%',
  height: '100vh'
}));
